/**
 * Workshop version of FrameFooter.
 *
 * Frame-level operations: Copy, Comment, Expand (always visible as icons),
 * and a More menu (···) containing destructive actions like Delete.
 * No React Flow or store dependencies.
 */

import { useState, useEffect, useRef } from "react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

interface FrameFooterProps {
  onCopy?: () => void;
  onComment?: () => void;
  onExpand?: () => void;
  onDelete?: () => void;
  showResize?: boolean;
  onResizeStart?: React.MouseEventHandler;
  className?: string;
  "data-testid"?: string;
}

export function FrameFooter({
  onCopy,
  onComment,
  onExpand,
  onDelete,
  showResize = true,
  onResizeStart,
  className,
  "data-testid": testId,
}: FrameFooterProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moreOpen]);

  return (
    <div
      className={cn(
        "flex h-6 w-full items-center border-t border-glass-stroke-faint px-2",
        className,
      )}
      data-testid={testId}
      data-frame-footer="true"
    >
      {/* Primary actions — always visible */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={onCopy}
          className="rounded p-1 text-muted transition-colors hover:text-secondary"
          aria-label="Copy frame"
          data-testid={testId ? `${testId}-copy` : undefined}
        >
          <Icons.Copy className="h-2.5 w-2.5" />
        </button>

        <button
          type="button"
          onClick={onComment}
          className="rounded p-1 text-muted transition-colors hover:text-secondary"
          aria-label="Comment on frame"
          data-testid={testId ? `${testId}-comment` : undefined}
        >
          <Icons.MessageSquare className="h-2.5 w-2.5" />
        </button>

        <button
          type="button"
          onClick={onExpand}
          className="rounded p-1 text-muted transition-colors hover:text-secondary"
          aria-label="Expand frame"
          data-testid={testId ? `${testId}-expand` : undefined}
        >
          <Icons.ZoomIn className="h-2.5 w-2.5" />
        </button>
      </div>

      <div className="flex-1" />

      {/* More menu + resize */}
      <div className="flex items-center gap-1">
        <div className="relative" ref={moreRef}>
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className={cn(
              "rounded p-1 transition-colors",
              moreOpen ? "text-secondary" : "text-muted hover:text-secondary",
            )}
            aria-label="More options"
            data-testid={testId ? `${testId}-more` : undefined}
          >
            <Icons.MoreHorizontal className="h-2.5 w-2.5" />
          </button>

          {moreOpen && (
            <div className="absolute bottom-full right-0 mb-1.5 min-w-[120px] rounded-md border border-glass-stroke bg-glass-fill-dense shadow-glass-sm backdrop-blur-glass-xs">
              <button
                type="button"
                onClick={() => {
                  onDelete?.();
                  setMoreOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-canvas-base text-muted transition-colors hover:text-status-error"
                data-testid={testId ? `${testId}-delete` : undefined}
              >
                <Icons.Trash2 className="h-3 w-3" />
                Delete frame
              </button>
            </div>
          )}
        </div>

        {showResize && (
          <button
            type="button"
            onMouseDown={onResizeStart}
            className="cursor-se-resize p-0.5"
            aria-label="Resize frame"
            data-testid={testId ? `${testId}-resize` : undefined}
          >
            <Icons.MoveDiagonal2 className="h-2 w-2 text-muted" />
          </button>
        )}
      </div>
    </div>
  );
}

export type { FrameFooterProps };
